import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "preact/hooks";
import { CircleCheck, Loader2, Star } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { NOTE_MAX_LENGTH, type RatingValue } from "shared/rate-products";
import { submitProductRatings, type RateableProduct } from "@/lib/api";
import {
  asksLifeBetter,
  buildWidgetRatingResponses,
  lifeBetterQuestion,
} from "@/lib/lifeBetter";
import {
  trackSubscriptionRatingPromptShown,
  trackSubscriptionRatingSubmitted,
  trackSubscriptionRatingDismissed,
  type SubscriptionRatingActionType,
} from "@/lib/tracking";
import { cn } from "@/lib/utils";
import { PILL_FILLED_CLASS } from "./CancelSubscriptionDialog";
import { FromSocialOcean } from "../FromSocialOcean";

const RATINGS: RatingValue[] = [1, 2, 3, 4, 5];

const RATING_HINTS: Record<RatingValue, string> = {
  1: "I don't like it",
  2: "Not for me",
  3: "It's okay",
  4: "I like it",
  5: "I love it",
};

// Where "See all your products and reviews" goes after a rating is posted.
const SOCIAL_OCEAN_BASE_URL =
  import.meta.env.VITE_PUBLIC_SOCIAL_OCEAN_BASE_URL || "https://socialocean.co";

/**
 * "See all your products and reviews". With the buyer's email, it opens
 * Social Ocean's sign-in with that email, the code already sent and the code
 * step showing — one step from their products. The email rides in the
 * fragment, which browsers never send to a server. We can't sign them in
 * outright: the widget has nothing that proves who they are.
 */
export function webAppHref(email: string | null | undefined): string {
  const trimmed = email?.trim();
  return trimmed
    ? `${SOCIAL_OCEAN_BASE_URL}/login/email#email=${encodeURIComponent(trimmed)}`
    : `${SOCIAL_OCEAN_BASE_URL}/products`;
}

// Smooth collapse/reveal shared by the success check and the action buttons.
const MOTION = "tw:transition-all tw:duration-500 tw:ease-out";

// The note box starts at three lines and grows with what's typed, up to six;
// past that it scrolls (same behaviour as the rate email's sheet, one size
// smaller to suit the dialog).
const NOTE_MIN_ROWS = 3;
const NOTE_MAX_ROWS = 6;

function fitNoteHeight(el: HTMLTextAreaElement) {
  const cs = getComputedStyle(el);
  const px = (v: string) => Number.parseFloat(v) || 0;
  const border = px(cs.borderTopWidth) + px(cs.borderBottomWidth);
  const lineHeight = px(cs.lineHeight) || 24;
  const max =
    lineHeight * NOTE_MAX_ROWS +
    px(cs.paddingTop) +
    px(cs.paddingBottom) +
    border;

  // `important` because the widget's Tailwind marks every utility !important,
  // and a plain inline height would lose to any height the cascade sets.
  el.style.setProperty("height", "auto", "important");
  const wanted = el.scrollHeight + border; // border-box: scrollHeight omits it
  el.style.setProperty("height", `${Math.min(wanted, max)}px`, "important");
  el.style.setProperty(
    "overflow-y",
    wanted > max ? "auto" : "hidden",
    "important",
  );
}

function RateRow({
  product,
  value,
  onPick,
  note,
  onNoteChange,
  disabled,
}: {
  product: RateableProduct;
  value: RatingValue | null;
  onPick: (v: RatingValue) => void;
  note: string;
  onNoteChange: (v: string) => void;
  disabled: boolean;
}) {
  const [hovered, setHovered] = useState<RatingValue | null>(null);
  const activeUpTo = hovered ?? value ?? 0;
  const hint = hovered ?? value;

  const noteRef = useRef<HTMLTextAreaElement>(null);
  const noteId = useId();
  // 4–5★ turns the note into the life-better question; the typed text stays
  // put across a change of rating and is filed by the final one at submit.
  const lifeBetter = asksLifeBetter(value);
  useLayoutEffect(() => {
    if (noteRef.current) fitNoteHeight(noteRef.current);
  }, [note, value]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:py-4">
      {/* Image on the left, product name + stars on the right. */}
      <div className="tw:flex tw:items-center tw:gap-4">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.title}
            className="tw:size-20 tw:shrink-0 tw:rounded-2xl tw:object-cover tw:shadow-[0_8px_24px_rgba(82,60,51,0.12)]"
          />
        ) : null}

        <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:items-start tw:text-left">
          <p className="tw:m-0 tw:max-w-full tw:truncate tw:text-[15px] tw:font-normal tw:text-[#1a1310]">
            {product.title}
          </p>

          {/* Stars + hint share a column sized to the stars' width, so the
              hint centers against the stars rather than the whole column.
              `relative` anchors the shimmer sweep below. */}
          <div className="tw:relative tw:mt-2 tw:flex tw:flex-col">
            <div
              className="tw:flex tw:items-center"
              role="radiogroup"
              aria-label={`Rate ${product.title}`}
            >
              {RATINGS.map((n) => {
                const active = activeUpTo >= n;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={value === n}
                    aria-label={`Rate ${n} out of 5`}
                    disabled={disabled}
                    onMouseEnter={() => {
                      if (!disabled) setHovered(n);
                    }}
                    onMouseLeave={() => {
                      if (!disabled) setHovered(null);
                    }}
                    onFocus={() => {
                      if (!disabled) setHovered(n);
                    }}
                    onBlur={() => {
                      if (!disabled) setHovered(null);
                    }}
                    onClick={() => onPick(n)}
                    className="tw:p-0.5 tw:transition-transform tw:disabled:cursor-default tw:disabled:pointer-events-none tw:hover:scale-110 tw:active:scale-95"
                  >
                    <Star
                      strokeWidth={1.5}
                      className="tw:size-8 tw:text-[#FAC8CA] tw:transition-colors tw:duration-150"
                      fill={active ? "currentColor" : "none"}
                    />
                  </button>
                );
              })}
            </div>

            <p className="tw:mt-1.5 tw:h-4 tw:w-full tw:text-center tw:text-xs tw:font-medium tw:text-muted-foreground tw:transition-opacity tw:duration-150">
              {hint != null ? RATING_HINTS[hint] : ""}
            </p>

            {/* Highlight sweep — clipped + non-interactive, scoped to the
                stars cluster (matches the PDP rate widget). */}
            <div
              aria-hidden="true"
              className="so-rate-flash tw:pointer-events-none tw:absolute tw:inset-0 tw:overflow-hidden"
            />
          </div>
        </div>
      </div>

      {/* The optional note only appears once a star is picked — the stars stay
          the one-tap ask, and the note is the follow-up right under them. Full
          row width: the stars column is too narrow to type in comfortably.
          After a happy rating it asks how the product makes life better (a
          visible label); otherwise it is the plain review note. */}
      {value != null ? (
        <div className="tw:flex tw:flex-col tw:gap-2">
          {lifeBetter ? (
            <label
              htmlFor={noteId}
              className="tw:m-0 tw:text-left tw:text-[15px] tw:font-medium tw:leading-5 tw:text-[#1a1310]"
            >
              {lifeBetterQuestion(product.title)}
            </label>
          ) : null}
          <textarea
            ref={noteRef}
            id={noteId}
            aria-label={
              lifeBetter ? undefined : `Add a note about ${product.title}`
            }
            value={note}
            onInput={(e) =>
              onNoteChange((e.target as HTMLTextAreaElement).value)
            }
            maxLength={NOTE_MAX_LENGTH}
            rows={NOTE_MIN_ROWS}
            disabled={disabled}
            placeholder="Share your experience"
            className="tw:block tw:w-full tw:rounded-xl tw:border tw:border-[#FAC8CA]/60 tw:bg-white tw:px-3 tw:py-2.5 tw:text-base tw:text-[#1a1310] tw:resize-none tw:outline-none tw:focus:border-[#FAC8CA] tw:transition-colors"
          />
        </div>
      ) : null}
    </div>
  );
}

// The thank-you's one button: same size and gradient in both states.
const THANKS_BUTTON_CLASS =
  "tw:flex tw:h-14 tw:w-full tw:items-center tw:justify-center tw:rounded-full tw:bg-[linear-gradient(90deg,#fbbf5b,#fc5899,#e44da3)] tw:px-4 tw:text-[15px] tw:font-bold tw:text-white tw:no-underline tw:shadow-[0_8px_18px_-10px_rgba(228,77,163,0.55)] tw:transition-[opacity,transform] tw:hover:opacity-95 tw:active:scale-[0.98] tw:focus-visible:outline-none tw:focus-visible:ring-4 tw:focus-visible:ring-[#F6A7AB]/50";

/** The rated product on the hero: its image, or a star when there is none. */
function HeroTile({ src, alt }: { src: string | null; alt: string }) {
  return (
    <div className="tw:relative tw:size-full tw:overflow-hidden tw:rounded-[24px] tw:bg-[#FDEDEE] tw:shadow-[0_22px_44px_-18px_rgba(194,91,130,0.55)] tw:ring-4 tw:ring-white">
      {src ? (
        <img
          key={src}
          src={src}
          alt={alt}
          className="so-ty-fade tw:absolute tw:inset-0 tw:size-full tw:object-cover"
        />
      ) : (
        <div className="tw:flex tw:size-full tw:items-center tw:justify-center">
          <Star
            strokeWidth={1.5}
            className="tw:size-12 tw:text-[#F6A7AB]"
            fill="currentColor"
          />
        </div>
      )}
    </div>
  );
}

/**
 * The thank-you after a rating. One centred column: the rated product on a
 * blush glow, one headline, one line, one button to all their products and
 * reviews. No points or gifts in this flow for now — a reward variant would
 * slot in here (see git history for the points-gift version).
 */
function RateThankYou({
  rated,
  allLink,
}: {
  rated: RateableProduct | undefined;
  allLink: string;
}) {
  return (
    <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-sm tw:flex-col tw:items-center tw:py-2 tw:text-center">
      <div className="so-ty-hero tw:relative tw:flex tw:h-44 tw:w-full tw:items-center tw:justify-center">
        <div
          aria-hidden="true"
          className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-1/2 tw:size-64 tw:[@media(max-height:760px)]:size-40 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:rounded-full tw:bg-[radial-gradient(circle,#FAC8CA_0%,#FDEDEE_38%,rgba(253,237,238,0)_68%)]"
        />
        <div className="tw:relative tw:size-32 tw:[@media(max-height:760px)]:size-20">
          <HeroTile src={rated?.imageUrl ?? null} alt={rated?.title ?? ""} />
        </div>
      </div>

      <div className="so-ty-fade tw:mt-5 tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-2">
        <h2 className="tw:m-0 tw:max-w-[18ch] tw:text-[26px] tw:font-bold tw:leading-[1.15] tw:tracking-[-0.02em] tw:text-balance tw:text-[#1a1310]">
          Thanks for sharing
        </h2>
        <p className="tw:m-0 tw:max-w-[30ch] tw:text-base tw:leading-6 tw:text-pretty tw:text-[#523c33]/70">
          You're helping others discover great products.
        </p>
      </div>

      <div className="tw:mt-7 tw:[@media(max-height:760px)]:mt-5 tw:h-14 tw:w-full">
        <a
          href={allLink}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(THANKS_BUTTON_CLASS, "so-ty-fade")}
        >
          <span className="tw:truncate">See all your products and reviews</span>
        </a>
      </div>
    </div>
  );
}

export interface ActionResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Headline for the completed action, e.g. "Subscription paused until June 30". */
  title: string;
  /** Optional supporting line under the title. */
  message?: string | null;
  actionType: SubscriptionRatingActionType;
  shop: string | null;
  /** Shopify customer id the rating is attributed to; null when unknown (rating hidden). */
  shopifyCustomerId: string | number | null;
  /**
   * Products the buyer can still rate on this shop (the rate email's list).
   * Prefetched when the manage sheet opens, so by the time we land here it's
   * usually ready and the rating block renders immediately. While loading, or
   * when there's nothing to rate, the dialog just shows the success message.
   */
  productsToRate: RateableProduct[] | undefined;
  /** The product the action was performed on — listed first when rateable. */
  focusProductId: number | null;
  /**
   * The buyer's email, when known: "See all your products and reviews" then
   * opens Social Ocean's sign-in on the code step for it (see webAppHref).
   */
  customerEmail?: string | null;
}

export function ActionResultDialog({
  open,
  onOpenChange,
  title,
  message,
  actionType,
  shop,
  shopifyCustomerId,
  productsToRate,
  focusProductId,
  customerEmail,
}: ActionResultDialogProps) {
  // The acted-on product first; the rest keep the server's (newest-first)
  // order. Empty when the buyer can't be attributed, which hides the section.
  const list: RateableProduct[] =
    shop != null && shopifyCustomerId != null && productsToRate != null
      ? (() => {
          const i = productsToRate.findIndex((p) => p.id === focusProductId);
          return i > 0
            ? [
                productsToRate[i],
                ...productsToRate.slice(0, i),
                ...productsToRate.slice(i + 1),
              ]
            : productsToRate;
        })()
      : [];
  const showRating = list.length > 0;
  // The product the prompt is "about" for analytics: the acted-on one when it
  // is in the list, otherwise whichever leads it.
  const leadProductId = list[0]?.id ?? null;

  // Rating picked per product id, and the optional note typed under it.
  const [ratings, setRatings] = useState<Record<number, RatingValue>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  // Drives the rate section's slide-up: starts below + transparent, then flips
  // on the frame after it mounts so the transition plays inside the open dialog.
  const [rateRevealed, setRateRevealed] = useState(false);
  const hasShownRef = useRef(false);
  // Focus the (non-tabbable) title on open instead of letting the dialog grab
  // the first star button.
  const initialFocusRef = useRef<HTMLParagraphElement>(null);

  // The review section, and the dialog box it sits in once the post lands (see
  // lockBoxHeight).
  const rateSectionRef = useRef<HTMLDivElement>(null);
  const lockedBoxRef = useRef<HTMLElement | null>(null);

  // Swapping the rating list for the thank-you would resize the whole dialog.
  // Pin the box's *minimum* height at its current height, so it never shrinks
  // (the thank-you centres in the space the list left) but can still grow and
  // scroll when the thank-you is taller, e.g. on short phone screens, instead
  // of squeezing the button under the footer. `important` because the widget's
  // Tailwind marks every utility !important.
  const lockBoxHeight = () => {
    const box = rateSectionRef.current?.closest<HTMLElement>(
      '[data-slot="dialog-content"]',
    );
    if (!box) return;
    box.style.setProperty("min-height", `${box.offsetHeight}px`, "important");
    lockedBoxRef.current = box;
  };

  // Reset rating state every time the dialog opens for a fresh action.
  useEffect(() => {
    if (open) {
      lockedBoxRef.current?.style.removeProperty("min-height");
      lockedBoxRef.current = null;
      setRatings({});
      setNotes({});
      setSubmitted(false);
      setError(false);
      hasShownRef.current = false;
    }
  }, [open]);

  // Play the rate section's slide-up once it's mounted and the dialog is open.
  // rAF guarantees the "from" state paints before we flip to revealed.
  useEffect(() => {
    if (!open || !showRating) {
      setRateRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setRateRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [open, showRating]);

  // Emit prompt_shown once, the first frame the rating block is actually visible
  // (the list may resolve a beat after the dialog opens).
  useEffect(() => {
    if (!open || leadProductId == null || hasShownRef.current) return;
    hasShownRef.current = true;
    trackSubscriptionRatingPromptShown({
      productId: leadProductId,
      actionType,
    });
  }, [open, leadProductId, actionType]);

  const submitMutation = useMutation({
    mutationFn: () => {
      if (shop == null || shopifyCustomerId == null) {
        return Promise.reject(new Error("not attributable"));
      }
      return submitProductRatings({
        shop,
        shopifyCustomerId,
        responses: buildWidgetRatingResponses(ratings, notes),
      });
    },
    onSuccess: () => {
      setError(false);
      lockBoxHeight();
      setSubmitted(true);
      for (const [id, rating] of Object.entries(ratings)) {
        trackSubscriptionRatingSubmitted({
          productId: Number(id),
          actionType,
          rating,
        });
      }
    },
    onError: () => setError(true),
  });

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen && leadProductId != null && !submitted) {
      trackSubscriptionRatingDismissed({
        productId: leadProductId,
        actionType,
      });
    }
    onOpenChange(nextOpen);
  };

  const hasSelection = Object.keys(ratings).length > 0;
  const ratingDisabled = submitted || submitMutation.isPending;
  // The success check recedes once the buyer engages with the rating, and the
  // Post button (then "Thank You!") takes its place — both ride the same
  // matched-height collapse/reveal motion. Stays collapsed post-submit.
  const checkCollapsed = showRating && hasSelection;
  const buttonsRevealed = showRating && hasSelection;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        // The thank-you stays until the buyer dismisses it (it used to vanish
        // after 3s, before anyone could read it), so give it a close button.
        showCloseButton={submitted}
        onOpenChange={handleClose}
        initialFocus={initialFocusRef}
        overlayClassName="tw:z-80"
        className={cn(
          "tw:z-81 tw:px-5 tw:max-w-[24rem] tw:rounded-3xl tw:max-h-[88dvh] tw:[@media(max-height:760px)]:max-h-[94dvh] tw:flex tw:flex-col tw:overflow-y-auto",
          showRating ? "tw:pt-8 tw:pb-2" : "tw:pt-6 tw:pb-6",
        )}
      >
        {/* Success check — collapses smoothly once a rating is picked. Its
            height/margin are matched by the Submit row below so the dialog's
            total height doesn't change on selection. */}
        <div
          className={cn(
            "tw:flex tw:justify-center tw:overflow-hidden tw:shrink-0",
            MOTION,
            checkCollapsed
              ? "tw:h-0 tw:opacity-0 tw:mb-0"
              : "tw:h-[4.75rem] tw:opacity-100 tw:mb-4",
          )}
        >
          <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3.5">
            <CircleCheck
              className="tw:size-12 tw:text-[#523D33]"
              strokeWidth={1.5}
            />
          </div>
        </div>

        <div className="tw:flex tw:shrink-0 tw:flex-col tw:items-center tw:text-center">
          <p
            ref={initialFocusRef}
            tabIndex={-1}
            className="tw:text-xl tw:font-medium tw:m-0 tw:text-[#1a1310] tw:outline-none"
          >
            {title}
          </p>
          {message ? (
            <p className="tw:text-sm tw:m-0 tw:mt-2 tw:text-gray-500 tw:max-w-65">
              {message}
            </p>
          ) : null}
        </div>

        {showRating ? (
          <div
            ref={rateSectionRef}
            className={cn(
              "tw:mt-10 tw:[@media(max-height:760px)]:mt-4 tw:shrink-0 tw:border-t tw:border-[#FAC8CA]/60 tw:pt-10 tw:[@media(max-height:760px)]:pt-4 tw:transition tw:duration-700 tw:ease-out",
              rateRevealed
                ? "tw:translate-y-0 tw:opacity-100"
                : "tw:translate-y-6 tw:opacity-0",
              // Posted: fill the pinned box and centre the thank-you in it.
              // Grow only: never shrink below the thank-you's own height, or
              // a short screen squeezes the button under the footer (the box
              // scrolls instead).
              submitted && "tw:flex tw:grow tw:flex-col tw:justify-center",
            )}
          >
            {submitted ? (
              <RateThankYou
                rated={list.find((p) => ratings[p.id] != null) ?? list[0]}
                allLink={webAppHref(customerEmail)}
              />
            ) : (
              <>
                <h2 className="tw:m-0 tw:text-center tw:text-xl tw:font-medium">
                  {list.length === 1
                    ? `Enjoying ${list[0].title}?`
                    : "Enjoying these products?"}
                </h2>

                <div className="tw:mt-2 tw:divide-y tw:divide-[#FAC8CA]/40">
                  {list.map((p) => (
                    <RateRow
                      key={p.id}
                      product={p}
                      value={ratings[p.id] ?? null}
                      onPick={(v) =>
                        setRatings((prev) => ({ ...prev, [p.id]: v }))
                      }
                      note={notes[p.id] ?? ""}
                      onNoteChange={(v) =>
                        setNotes((prev) => ({ ...prev, [p.id]: v }))
                      }
                      disabled={ratingDisabled}
                    />
                  ))}
                </div>

                {error ? (
                  <p className="tw:mt-2.5 tw:text-center tw:text-xs tw:text-[#b42318]">
                    We couldn&apos;t save that. Try again.
                  </p>
                ) : null}

                {/* Post button — revealed once a star is picked, matched in
                    height/margin to the success check's collapse so the dialog's
                    total height stays constant. Sticky so it stays reachable
                    while scrolling a long list. */}
                <div
                  className={cn(
                    "tw:sticky tw:bottom-0 tw:w-full tw:overflow-hidden tw:bg-white",
                    MOTION,
                    buttonsRevealed
                      ? "tw:h-[4.75rem] tw:opacity-100 tw:mt-2"
                      : "tw:h-0 tw:opacity-0 tw:mt-0",
                  )}
                >
                  <div className="tw:flex tw:h-full tw:items-center tw:justify-center">
                    <Button
                      type="button"
                      disabled={!hasSelection || submitMutation.isPending}
                      onClick={() => {
                        if (hasSelection) submitMutation.mutate(undefined);
                      }}
                      className={cn(PILL_FILLED_CLASS, "tw:w-full")}
                    >
                      {submitMutation.isPending ? (
                        <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
                      ) : (
                        "Post"
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <Button
            type="button"
            onClick={() => handleClose(false)}
            className={cn(PILL_FILLED_CLASS, "tw:mt-6")}
          >
            Done
          </Button>
        )}

        {/* Social Ocean attribution is only relevant to the rating prompt. */}
        {showRating ? (
          <div className="tw:mt-8 tw:flex tw:shrink-0 tw:justify-center">
            <FromSocialOcean className="tw:object-contain tw:block tw:w-24 tw:opacity-70" />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
